/**
 * Upcoming Event banner — Fiber site only.
 *
 * Uses Logic Hop for region detection:
 *   - Conditions: is-in-americas, is-in-asian-pacific, is-in-emea
 *
 * First visit (no stored region):
 *   Default banner shown with the next global event. On geolocation accept,
 *   waits for Logic Hop, checks conditions sequentially, stores the region
 *   in sessionStorage, and swaps in the matching pre-rendered event.
 *
 * Subsequent page loads (region in sessionStorage):
 *   JS immediately swaps to the stored region's content — no flash, no
 *   Logic Hop round-trip needed.
 *
 * Dismissal (close button, Event Details click, or landing on the event the
 * banner is promoting) is stored in sessionStorage, so it persists across
 * pages but resets when the browser tab/session is closed.
 */
document.addEventListener('DOMContentLoaded', () => {
    const DISMISS_KEY = 'weidmann_event_banner_dismissed';
    const REGION_KEY = 'weidmann_event_region';

    const REGION_CONDITIONS = [
        { slug: 'americas', condition: 'is-in-americas' },
        { slug: 'asian-pacific', condition: 'is-in-asian-pacific' },
        { slug: 'emea', condition: 'is-in-emea' },
    ];

    const banner = document.querySelector('[data-upcoming-event-banner]');
    if (!banner) return;

    // If the user has dismissed the banner this session, keep it hidden.
    if (sessionStorage.getItem(DISMISS_KEY)) return;

    const dismiss = () => {
        sessionStorage.setItem(DISMISS_KEY, '1');
        banner.setAttribute('hidden', '');
    };

    const currentEventId = parseInt(banner.dataset.currentEventId || '0', 10);

    const hideIfViewingCurrentEvent = () => {
        if (!currentEventId) return false;

        const visible = banner.querySelector('.upcoming-event-banner__item:not([hidden])');
        if (visible && parseInt(visible.dataset.eventId || '0', 10) === currentEventId) {
            dismiss();
            return true;
        }
        return false;
    };

    /**
     * Swap the visible banner text to the matching region's pre-rendered content.
     */
    const showRegion = (slug) => {
        const regionDiv = banner.querySelector(`[data-region="${slug}"]`);
        if (!regionDiv) return;

        const defaultDiv = banner.querySelector('[data-default-text]');
        if (defaultDiv) defaultDiv.setAttribute('hidden', '');

        regionDiv.removeAttribute('hidden');

        // A post-consent swap may land on the event the visitor is viewing.
        hideIfViewingCurrentEvent();
    };

    // If the region was already resolved this session, swap immediately
    // before the banner becomes visible — prevents flash of default content.
    const storedRegion = sessionStorage.getItem(REGION_KEY);
    if (storedRegion) {
        showRegion(storedRegion);
    }

    // Already on the promoted event's page: keep it closed, don't reveal.
    if (hideIfViewingCurrentEvent()) return;

    banner.removeAttribute('hidden');

    // Close button.
    const closeBtn = banner.querySelector('[data-upcoming-event-banner-close]');
    if (closeBtn) {
        closeBtn.addEventListener('click', () => dismiss());
    }

    // Dismiss when user clicks any Event Details link.
    // Only set the flag — don't hide the banner or prevent navigation.
    banner.querySelectorAll('a.upcoming-event-banner__cta').forEach((link) => {
        link.addEventListener('click', () => sessionStorage.setItem(DISMISS_KEY, '1'));
    });

    // If region is already known, no need to run Logic Hop again.
    if (storedRegion) return;

    /**
     * Check a Logic Hop condition. Returns a Promise<boolean>.
     */
    const checkCondition = (conditionSlug) =>
        new Promise((resolve) => {
            if (typeof window.logichop_condition === 'undefined') {
                resolve(false);
                return;
            }

            logichop_condition(
                conditionSlug,
                () => resolve(true),
                () => resolve(false)
            );
        });

    /**
     * Check conditions one by one. When one matches, store it and swap content.
     */
    const checkConditions = (index) => {
        if (index >= REGION_CONDITIONS.length) return;

        const { slug, condition } = REGION_CONDITIONS[index];
        checkCondition(condition).then((matched) => {
            if (matched) {
                sessionStorage.setItem(REGION_KEY, slug);
                showRegion(slug);
            } else {
                checkConditions(index + 1);
            }
        });
    };

    /**
     * Wait for Logic Hop to finish its init AJAX call.
     * Logic Hop sets logichop.data.loaded = true once ready.
     */
    const waitForLogicHop = (callback, attempts) => {
        attempts = attempts || 0;
        if (typeof window.logichop !== 'undefined' && logichop.data && logichop.data.loaded) {
            callback();
            return;
        }
        if (attempts >= 50) return;
        setTimeout(() => waitForLogicHop(callback, attempts + 1), 200);
    };

    /**
     * Check conditions and swap in the matching region content.
     */
    const resolveRegion = () => {
        waitForLogicHop(() => checkConditions(0));
    };

    resolveRegion();

    // If Logic Hop wasn't ready on page load (no consent cookie yet), the
    // bridging cookie will initialize it after CookieScript consent and
    // dispatch 'logichop-ready' once parseLogic completes.
    window.addEventListener('logichop-ready', () => {
        checkConditions(0);
    });
});
